/*!
*   @mosaik/presentation v2.2.5
*   (c) 2022 Serviceware SE (https://www.serviceware.se)
*   © 2015-2020 Serviceware SE license (see LICENSE)
*   dependencies:
*      tslib
*   peer dependencies:
*      @mosaik/core
*/
import { __awaiter } from 'tslib';

/*!
*   @mosaik/core v2.2.12
*   (c) 2022 Serviceware SE (https://www.serviceware.se)
*   © 2015-2020 Serviceware SE license (see LICENSE)
*   dependencies:
*      tslib
*/

/**
 * @public
 */
class EventArgs {
    //#region Fields
    static get empty() {
        return new EventArgs();
    }
}

/**
 * @public
 */
class Event {
    //#endregion
    //#region Ctor
    constructor(invoker) {
        //#region Fields
        this._callbacks = [];
        this._scopes = [];
        invoker.apply((sender, args) => this.invoke(sender, args));
    }
    //#endregion
    //#region Properties
    get has() {
        return this._callbacks.length > 0;
    }
    //#endregion
    //#region Methods
    add(callback, scope) {
        this._callbacks.push(callback);
        this._scopes.push(scope);
    }
    remove(callback, scope) {
        const cbs = this._callbacks;
        const scopes = this._scopes;
        let search = cbs.length - 1;
        while (search > -1) {
            search = cbs.lastIndexOf(callback, search);
            if (scopes[search] === scope) {
                cbs.splice(search, 1);
                scopes.splice(search, 1);
            }
            search--;
        }
    }
    invoke(sender, args) {
        const cbs = this._callbacks.slice(0);
        const len = cbs.length;
        for (let i = 0; i < len; i++) {
            const scopes = this._scopes.slice(0);
            cbs[i].call(scopes[i], sender, args);
        }
    }
}

/**
 * @public
 */
class FunctionExtensions {
    static noop(...args) {
        // empty
    }
    static trueop(...args) {
        return true;
    }
    static falseop(...args) {
        return false;
    }
    static debounce(func, wait, immediate, ...args) {
        wait = wait || 0;
        immediate = immediate || false;
        let token;
        return () => {
            const callNow = immediate && !token;
            clearTimeout(token);
            token = setTimeout(() => {
                token = 0;
                if (!immediate) {
                    func(args);
                }
            }, wait);
            if (callNow) {
                func(args);
            }
        };
    }
    static delay(func, wait, ...args) {
        wait = wait || 0;
        let token;
        return () => {
            clearTimeout(token);
            token = setTimeout(() => {
                func(args);
            }, wait);
        };
    }
}

/**
 * @public
 */
class EventInvoker {
    //#endregion
    //#region Ctor
    constructor() {
        this._fn = FunctionExtensions.noop;
    }
    //#endregion
    //#region Methods
    apply(fn) {
        this._fn = fn;
    }
    invoke(sender, args) {
        this._fn(sender, args);
    }
}

/**
 * @public
 */
class Exception extends Error {
    //#endregion
    //#region Ctor
    constructor(message) {
        super(message);
        this._name = '';
        this._stack = '';
        this._message = message || '';
        Object.defineProperty(this, 'name', {
            value: new.target.name,
            enumerable: false,
        });
        // see: https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        const prototype = new.target.prototype;
        const setPrototypeOf = Object.setPrototypeOf;
        setPrototypeOf ? setPrototypeOf(this, prototype) : (this.__proto__ = prototype);
        const captureStackTrace = Error.captureStackTrace;
        if (captureStackTrace) {
            captureStackTrace(this, this.constructor);
        }
    }
    //#endregion
    //#region Properties
    get name() { return this._name; }
    set name(value) { this._name = value; }
    get stack() { return this._stack; }
    set stack(value) { this._stack = value; }
    get message() { return this._message; }
    //#endregion
    //#region Methods
    static fromError(error) {
        const ex = new Exception(error.message);
        ex.name = error.name;
        ex.stack = error.stack;
        return ex;
    }
    toString() {
        const typeName = this.constructor.name;
        return typeName ? typeName + ': ' + this._message : this._message;
    }
}

/**
 * @public
 */
class KeyNotFoundException extends Exception {
    //#region Ctor
    constructor(message = 'The given key was not present in the map.') {
        super(message);
    }
}

/**
 * @public
 */
class ArgumentException extends Exception {
    //#endregion
    //#region Ctor
    constructor(message, paramName) {
        super(message || 'The value is outside the expected range.');
        this._paramName = paramName || '';
    }
    //#endregion
    //#region Properties
    get paramName() {
        return this._paramName;
    }
    //#endregion
    //#region Methods
    toString() {
        return super.toString() + (this.paramName ? '\r\n' + `parameter name: ${this.paramName}` : '');
    }
}

/**
 * @public
 */
class ArgumentNullException extends ArgumentException {
    //#region Ctor
    constructor(paramName, message) {
        super(message || 'The value can not be null.', paramName);
    }
}

/**
 * @public
 */
class ArgumentOutOfRangeException extends ArgumentException {
    //#endregion
    //#region Ctor
    constructor(paramName, actualValue, message) {
        super(message || 'The specified argument is outside the valid value range.', paramName);
        this._actualValue = actualValue;
    }
    //#endregion
    //#region Properties
    get actualValue() {
        return this._actualValue;
    }
    //#endregion
    //#region Methods
    toString() {
        return super.toString() + (this.actualValue ? '\r\n' + `actual value: ${this.actualValue}` : '');
    }
}

/**
 * @public
 */
class Guard {
    //#region Methods
    static isNotNull(value, paramName, message) {
        this.assert(!!value, () => { throw new ArgumentNullException(paramName, message); });
    }
    static isNotNullOrEmpty(value, paramName, message) {
        this.assert(!!value, () => { throw new ArgumentNullException(paramName, message); });
    }
    static isGreaterThanZero(value, paramName, message) {
        this.assert(value > 0, () => { throw new ArgumentOutOfRangeException(paramName, value, message); });
    }
    static isLessThanZero(value, paramName, message) {
        this.assert(value < 0, () => { throw new ArgumentOutOfRangeException(paramName, value, message); });
    }
    static isNotValid(precondition, paramName, message) {
        this.assert(!precondition, () => { throw new ArgumentException(message, paramName); });
    }
    static isLessThanOrGreaterThan(min, max, value, paramName, message) {
        this.assert(value < min || value > max, () => { throw new ArgumentException(message || `Value is not valid for '${value}'. '${value}' should be greater than or equal to '${min}' and less than or equal to '${max}'.`, paramName); });
    }
    static assert(condition, throwAction) {
        if (!condition) {
            throwAction();
        }
    }
}

/**
 * @public
 */
class CacheRegistrar {
    //#endregion
    //#region Ctor
    constructor() {
        this._cache = new Map();
        this._registrationsEventInvoker = new EventInvoker();
        this.registrationsChangedEvent = new Event(this._registrationsEventInvoker);
    }
    get registrations() {
        return this._cache;
    }
    //#endregion
    //#region Methods
    get(key) {
        Guard.isNotNull(key, 'key');
        if (this._cache.has(key)) {
            return this._cache.get(key);
        }
        else {
            throw new KeyNotFoundException();
        }
    }
    has(key) {
        return this._cache.has(key);
    }
    set(key, value) {
        Guard.isNotNull(key, 'key');
        Guard.isNotNull(value, 'value');
        this._cache.set(key, value);
        this.onRegistrationsChanged();
    }
    setMany(values) {
        Guard.isNotNull(values, 'values');
        values.forEach((x, y) => this.set(y, x));
    }
    remove(key) {
        Guard.isNotNull(key, 'key');
        this._cache.delete(key);
        this.onRegistrationsChanged();
    }
    removeMany(keys) {
        Guard.isNotNull(keys, 'keys');
        keys.forEach(x => this.remove(x));
    }
    clear() {
        this._cache.clear();
        this.onRegistrationsChanged();
    }
    onRegistrationsChanged() {
        this._registrationsEventInvoker.invoke(this, EventArgs.empty);
    }
}

/**
 * @public
 */
class ValueEventArgs extends EventArgs {
    //#endregion
    //#region Ctor
    constructor(value) {
        super();
        this._value = value;
    }
    //#endregion
    //#region Properties
    get value() {
        return this._value;
    }
}

/**
 * @public
 */
var HttpStatus;
(function (HttpStatus) {
    HttpStatus[HttpStatus["Continue"] = 100] = "Continue";
    HttpStatus[HttpStatus["SwitchingProtocols"] = 101] = "SwitchingProtocols";
    HttpStatus[HttpStatus["Ok"] = 200] = "Ok";
    HttpStatus[HttpStatus["Created"] = 201] = "Created";
    HttpStatus[HttpStatus["Accepted"] = 202] = "Accepted";
    HttpStatus[HttpStatus["NonAuthoritativeInformation"] = 203] = "NonAuthoritativeInformation";
    HttpStatus[HttpStatus["NoContent"] = 204] = "NoContent";
    HttpStatus[HttpStatus["ResetContent"] = 205] = "ResetContent";
    HttpStatus[HttpStatus["PartialContent"] = 206] = "PartialContent";
    HttpStatus[HttpStatus["MultipleChoices"] = 300] = "MultipleChoices";
    HttpStatus[HttpStatus["MovedPermanently"] = 301] = "MovedPermanently";
    HttpStatus[HttpStatus["Found"] = 302] = "Found";
    HttpStatus[HttpStatus["SeeOther"] = 303] = "SeeOther";
    HttpStatus[HttpStatus["NotModified"] = 304] = "NotModified";
    HttpStatus[HttpStatus["UseProxy"] = 305] = "UseProxy";
    HttpStatus[HttpStatus["TemporaryRedirect"] = 307] = "TemporaryRedirect";
    HttpStatus[HttpStatus["BadRequest"] = 400] = "BadRequest";
    HttpStatus[HttpStatus["Unauthorized"] = 401] = "Unauthorized";
    HttpStatus[HttpStatus["PaymentRequired"] = 402] = "PaymentRequired";
    HttpStatus[HttpStatus["Forbidden"] = 403] = "Forbidden";
    HttpStatus[HttpStatus["NotFound"] = 404] = "NotFound";
    HttpStatus[HttpStatus["MethodNotAllowed"] = 405] = "MethodNotAllowed";
    HttpStatus[HttpStatus["NotAcceptable"] = 406] = "NotAcceptable";
    HttpStatus[HttpStatus["ProxyAuthenticationRequired"] = 407] = "ProxyAuthenticationRequired";
    HttpStatus[HttpStatus["RequestTimeout"] = 408] = "RequestTimeout";
    HttpStatus[HttpStatus["Conflict"] = 409] = "Conflict";
    HttpStatus[HttpStatus["Gone"] = 410] = "Gone";
    HttpStatus[HttpStatus["LengthRequired"] = 411] = "LengthRequired";
    HttpStatus[HttpStatus["PreconditionFailed"] = 412] = "PreconditionFailed";
    HttpStatus[HttpStatus["RequestEntityTooLarge"] = 413] = "RequestEntityTooLarge";
    HttpStatus[HttpStatus["RequestUriTooLong"] = 414] = "RequestUriTooLong";
    HttpStatus[HttpStatus["UnsupportedMediaType"] = 415] = "UnsupportedMediaType";
    HttpStatus[HttpStatus["RequestedRangeNotSatisfiable"] = 416] = "RequestedRangeNotSatisfiable";
    HttpStatus[HttpStatus["ExpectationFailed"] = 417] = "ExpectationFailed";
    HttpStatus[HttpStatus["UnprocessableEntity"] = 422] = "UnprocessableEntity";
    HttpStatus[HttpStatus["FailedDependency"] = 424] = "FailedDependency";
    HttpStatus[HttpStatus["TooManyRequests"] = 429] = "TooManyRequests";
    HttpStatus[HttpStatus["UnavailableForLegalReasons"] = 451] = "UnavailableForLegalReasons";
    HttpStatus[HttpStatus["InternalServerError"] = 500] = "InternalServerError";
    HttpStatus[HttpStatus["NotImplemented"] = 501] = "NotImplemented";
    HttpStatus[HttpStatus["BadGateway"] = 502] = "BadGateway";
    HttpStatus[HttpStatus["ServiceUnavailable"] = 503] = "ServiceUnavailable";
    HttpStatus[HttpStatus["GatewayTimeout"] = 504] = "GatewayTimeout";
    HttpStatus[HttpStatus["HttpVersionNotSupported"] = 505] = "HttpVersionNotSupported";
    HttpStatus[HttpStatus["InsufficientStorage"] = 507] = "InsufficientStorage";
})(HttpStatus || (HttpStatus = {}));

/**
 * @public
 */
class Guid {
    //#endregion
    //#region Ctor
    constructor(guid) {
        if (!guid) {
            throw new Exception('Argument \'guid\' could not be empty!');
        }
        if (!Guid.isGuid(guid.toLowerCase())) {
            throw new Exception('Argument \'guid\' is not a valid guid!');
        }
        this._guid = guid.toLowerCase();
    }
    //#region Fields
    static get empty() {
        return Guid._emptyGuid;
    }
    static newGuid() {
        let uuid = '';
        let random;
        for (let i = 0; i < 32; i++) {
            random = Math.random() * 16 | 0;
            if (i === 8 || i === 12 || i === 16 || i === 20) {
                uuid += '-';
            }
            uuid += (i === 12 ? 4 : (i === 16 ? (random & 3 | 8) : random)).toString(16);
        }
        return new Guid(uuid);
    }
    static isGuid(guid) {
        return Guid._expression.test(guid);
    }
    static parse(guid) {
        if (!guid) {
            throw new Exception('Argument \'guid\' could not be empty!');
        }
        if (!Guid.isGuid(guid.toLowerCase())) {
            throw new Exception('Argument \'guid\' is not a valid guid!');
        }
        return new Guid(guid);
    }
    //#endregion
    //#region Methods
    toString() {
        return this._guid;
    }
    valueOf() {
        return this._guid;
    }
}
Guid._expression = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
Guid._emptyGuid = new Guid('00000000-0000-0000-0000-000000000000');

/**
 * @public
 */
class StringBuilder {
    //#endregion
    //#region Ctor
    constructor(value) {
        this._list = new Array();
        if (value) {
            this._list.push(value);
        }
    }
    //#endregion
    //#region Properties
    get length() {
        let l = 0;
        for (const str of this._list) {
            l += str.length;
        }
        return l;
    }
    //#endregion
    //#region Methods
    append(value, repeatCount) {
        repeatCount = repeatCount || 0;
        for (let i = 0; i < repeatCount; i++) {
            this._list.push(value);
        }
        if (!repeatCount) {
            this._list.push(value);
        }
        return this;
    }
    appendLine(value) {
        if (value) {
            this.append(value);
        }
        return this.append('\r\n');
    }
    appendFormat(value, ...args) {
        for (let i = 0; i < args.length; i++) {
            const find = `{${i}}`;
            const regex = new RegExp(this.escapeRegExp(find), 'g');
            value = value.replace(regex, args[i]);
        }
        return this.append(value);
    }
    appendSubstring(value, startIndex, count) {
        if (startIndex < 0) {
            throw new ArgumentOutOfRangeException('startIndex');
        }
        if (count < 0) {
            throw new ArgumentOutOfRangeException('count');
        }
        if (!value) {
            if (startIndex === 0 && count === 0) {
                throw new ArgumentNullException('startIndex');
            }
            if (count === 0) {
                throw new ArgumentNullException('count');
            }
        }
        if (count === 0) {
            return this;
        }
        if (startIndex > value.length - count) {
            throw new ArgumentNullException('startIndex');
        }
        this.append(value.substring(startIndex, startIndex + count));
        return this;
    }
    clear() {
        this._list.length = 0;
        this._list.splice(0, this._list.length);
        return this;
    }
    toString() {
        let result = '';
        for (const iterator of this._list) {
            result += iterator;
        }
        return result;
    }
    escapeRegExp(str) {
        return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class Command {
    //#endregion
    //#region Ctor
    constructor(executeMethod, canExecuteMethod) {
        this._executeMethod = executeMethod;
        this._canExecuteMethod = canExecuteMethod || FunctionExtensions.trueop;
    }
    //#endregion
    //#region Methods
    execute(parameter) {
        if (this._executeMethod) {
            if ((this._canExecuteMethod && this._canExecuteMethod(parameter)) || !this._canExecuteMethod) {
                this._executeMethod(parameter);
            }
        }
    }
    canExecute(parameter) {
        if (this._canExecuteMethod) {
            return this._canExecuteMethod(parameter);
        }
        return true;
    }
}

/**
 * @public
 */
class CommandAggregator extends CacheRegistrar {
    //#region Ctor
    constructor() {
        super();
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class PropertyChangedEventArgs extends EventArgs {
    //#endregion
    //#region Ctor
    constructor(oldValue, newValue) {
        super();
        this._oldValue = oldValue;
        this._newValue = newValue;
    }
    //#endregion
    //#region Properties
    get oldValue() { return this._oldValue; }
    get newValue() { return this._newValue; }
}

//#region Imports
//#endregion
/**
 * @public
 */
class QuerySelectorElementException extends Exception {
    //#endregion
    //#region Ctor
    constructor(selector, message) {
        super(message || `A element with selector '${selector || '?'}' was not found.`);
        this._selector = selector || '';
    }
    //#endregion
    //#region Properties
    get selector() { return this._selector; }
}

/**
 * @public
 */
class DOMTokenListExtensions {
    //#region Methods
    static toggle(self, trueToken, falseToken, condition) {
        if (condition) {
            if (falseToken && self.contains(falseToken)) {
                self.remove(falseToken);
            }
            if (trueToken) {
                self.add(trueToken);
            }
        }
        else {
            if (trueToken && self.contains(trueToken)) {
                self.remove(trueToken);
            }
            if (falseToken) {
                self.add(falseToken);
            }
        }
    }
}

/**
 * @public
 */
class NamedNodeMapExtensions {
    //#region Methods
    static toggle(self, trueToken, falseToken, condition) {
        if (condition) {
            if (NamedNodeMapExtensions.toArray(self).find(x => x.localName === falseToken.qualifiedName && x.value === falseToken.value)) {
                self.removeNamedItem(falseToken.qualifiedName);
            }
            if (trueToken) {
                const attr = document.createAttribute(trueToken.qualifiedName);
                attr.value = trueToken.value.toString();
                self.setNamedItem(attr);
            }
        }
        else {
            if (NamedNodeMapExtensions.toArray(self).find(x => x.localName === trueToken.qualifiedName && x.value === trueToken.value)) {
                self.removeNamedItem(trueToken.qualifiedName);
            }
            if (falseToken) {
                const attr = document.createAttribute(falseToken.qualifiedName);
                attr.value = falseToken.value.toString();
                self.setNamedItem(attr);
            }
        }
    }
    static toArray(self) {
        const array = new Array();
        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < self.length; i++) {
            array.push(self[i]);
        }
        return array;
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class ElementExtensions {
    //#region Methods
    //#region Attribures
    static hasAttribute(self, attribute) {
        return ElementExtensions.hasAttributes(self, [attribute]);
    }
    static hasAttributes(self, attributes) {
        return attributes.some(x => {
            const altForms = [x, `data-${x}`, `x-${x}`];
            for (let j = 0; altForms[j]; j += 1) {
                if (self.hasAttribute(altForms[j])) {
                    return true;
                }
            }
            return false;
        });
    }
    static toggleAttribute(self, trueToken, falseToken, condition) {
        NamedNodeMapExtensions.toggle(self.attributes, trueToken, falseToken, condition);
    }
    //#endregion
    //#region Classes
    static toggleClass(self, trueToken, falseToken, condition) {
        DOMTokenListExtensions.toggle(self.classList, trueToken, falseToken, condition);
    }
    static replaceClass(self, oldToken, newToken) {
        if (oldToken && self.classList.contains(oldToken)) {
            self.classList.replace(oldToken, newToken);
        }
        else {
            self.classList.add(newToken);
        }
    }
    //#endregion
    //#region Events
    static subscribe(self, type, handler, options) {
        self.addEventListener(type, (x) => handler.call(ElementExtensions, x), options);
    }
    static unsubscribe(self, type, handler, options) {
        self.removeEventListener(type, handler, options);
    }
}

/**
 * @public
 */
var MediaQueryListExtensions;
(function (MediaQueryListExtensions) {
    /**
     * Fix for `ie` and `safari`.
     * The `addListener` method is deprecated, it is recomended to use the `addEventListener` method.
     * But `addEventListener` method is not supported by `ie` and `safari`.
     * @workaround
     */
    function addChangeListener(mediaQueryList, listener) {
        if (Object.is(mediaQueryList, 'addListener')) {
            mediaQueryList.addEventListener('change', listener);
        }
        else {
            // tslint:disable-next-line: deprecation
            mediaQueryList.addListener(listener);
        }
    }
    MediaQueryListExtensions.addChangeListener = addChangeListener;
    /**
     * Fix for `ie` and `safari`.
     * The `removeListener` method is deprecated, it is recomended to use the `removeEventListener` method.
     * But `removeEventListener` method is not supported by `ie' and `safari'.
     * @workaround
     */
    function removeChangeListener(mediaQueryList, listener) {
        if (Object.is(mediaQueryList, 'removeEventListener')) {
            mediaQueryList.removeEventListener('change', listener);
        }
        else {
            // tslint:disable-next-line: deprecation
            mediaQueryList.removeListener(listener);
        }
    }
    MediaQueryListExtensions.removeChangeListener = removeChangeListener;
})(MediaQueryListExtensions || (MediaQueryListExtensions = {}));

/**
 * @public
 */
class StringExtensions {
    //#region Methods
    static escape(self) {
        const reUnescapedHtml = /[&<>"']/g;
        const reHasUnescapedHtml = RegExp(reUnescapedHtml.source);
        const htmlEscapes = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
        };
        return (self && reHasUnescapedHtml.test(self))
            ? self.replace(reUnescapedHtml, chr => htmlEscapes[chr])
            : (self || '');
    }
    static unescape(self) {
        const reEscapedHtml = /&(?:amp|lt|gt|quot|#(0+)?39);/g;
        const reHasEscapedHtml = RegExp(reEscapedHtml.source);
        const htmlUnescapes = {
            '&amp;': '&',
            '&lt;': '<',
            '&gt;': '>',
            '&quot;': '"',
            '&#39;': "'"
        };
        return (self && reHasEscapedHtml.test(self))
            ? self.replace(reEscapedHtml, entity => (htmlUnescapes[entity] || "'"))
            : (self || '');
    }
}

/**
 * @public
 */
var Key;
(function (Key) {
    Key[Key["Backspace"] = 8] = "Backspace";
    Key[Key["Tab"] = 9] = "Tab";
    Key[Key["Enter"] = 13] = "Enter";
    Key[Key["Shift"] = 16] = "Shift";
    Key[Key["Ctrl"] = 17] = "Ctrl";
    Key[Key["Alt"] = 18] = "Alt";
    Key[Key["PauseBreak"] = 19] = "PauseBreak";
    Key[Key["CapsLock"] = 20] = "CapsLock";
    Key[Key["Escape"] = 27] = "Escape";
    Key[Key["Space"] = 32] = "Space";
    Key[Key["PageUp"] = 33] = "PageUp";
    Key[Key["PageDown"] = 34] = "PageDown";
    Key[Key["End"] = 35] = "End";
    Key[Key["Home"] = 36] = "Home";
    Key[Key["LeftArrow"] = 37] = "LeftArrow";
    Key[Key["UpArrow"] = 38] = "UpArrow";
    Key[Key["RightArrow"] = 39] = "RightArrow";
    Key[Key["DownArrow"] = 40] = "DownArrow";
    Key[Key["Insert"] = 45] = "Insert";
    Key[Key["Delete"] = 46] = "Delete";
    Key[Key["Zero"] = 48] = "Zero";
    Key[Key["ClosedParen"] = 48] = "ClosedParen";
    Key[Key["One"] = 49] = "One";
    Key[Key["ExclamationMark"] = 49] = "ExclamationMark";
    Key[Key["Two"] = 50] = "Two";
    Key[Key["AtSign"] = 50] = "AtSign";
    Key[Key["Three"] = 51] = "Three";
    Key[Key["PoundSign"] = 51] = "PoundSign";
    Key[Key["Hash"] = 51] = "Hash";
    Key[Key["Four"] = 52] = "Four";
    Key[Key["DollarSign"] = 52] = "DollarSign";
    Key[Key["Five"] = 53] = "Five";
    Key[Key["PercentSign"] = 53] = "PercentSign";
    Key[Key["Six"] = 54] = "Six";
    Key[Key["Caret"] = 54] = "Caret";
    Key[Key["Hat"] = 54] = "Hat";
    Key[Key["Seven"] = 55] = "Seven";
    Key[Key["Ampersand"] = 55] = "Ampersand";
    Key[Key["Eight"] = 56] = "Eight";
    Key[Key["Star"] = 56] = "Star";
    Key[Key["Asterik"] = 56] = "Asterik";
    Key[Key["Nine"] = 57] = "Nine";
    Key[Key["OpenParen"] = 57] = "OpenParen";
    Key[Key["A"] = 65] = "A";
    Key[Key["B"] = 66] = "B";
    Key[Key["C"] = 67] = "C";
    Key[Key["D"] = 68] = "D";
    Key[Key["E"] = 69] = "E";
    Key[Key["F"] = 70] = "F";
    Key[Key["G"] = 71] = "G";
    Key[Key["H"] = 72] = "H";
    Key[Key["I"] = 73] = "I";
    Key[Key["J"] = 74] = "J";
    Key[Key["K"] = 75] = "K";
    Key[Key["L"] = 76] = "L";
    Key[Key["M"] = 77] = "M";
    Key[Key["N"] = 78] = "N";
    Key[Key["O"] = 79] = "O";
    Key[Key["P"] = 80] = "P";
    Key[Key["Q"] = 81] = "Q";
    Key[Key["R"] = 82] = "R";
    Key[Key["S"] = 83] = "S";
    Key[Key["T"] = 84] = "T";
    Key[Key["U"] = 85] = "U";
    Key[Key["V"] = 86] = "V";
    Key[Key["W"] = 87] = "W";
    Key[Key["X"] = 88] = "X";
    Key[Key["Y"] = 89] = "Y";
    Key[Key["Z"] = 90] = "Z";
    Key[Key["LeftWindowKey"] = 91] = "LeftWindowKey";
    Key[Key["RightWindowKey"] = 92] = "RightWindowKey";
    Key[Key["SelectKey"] = 93] = "SelectKey";
    Key[Key["Numpad0"] = 96] = "Numpad0";
    Key[Key["Numpad1"] = 97] = "Numpad1";
    Key[Key["Numpad2"] = 98] = "Numpad2";
    Key[Key["Numpad3"] = 99] = "Numpad3";
    Key[Key["Numpad4"] = 100] = "Numpad4";
    Key[Key["Numpad5"] = 101] = "Numpad5";
    Key[Key["Numpad6"] = 102] = "Numpad6";
    Key[Key["Numpad7"] = 103] = "Numpad7";
    Key[Key["Numpad8"] = 104] = "Numpad8";
    Key[Key["Numpad9"] = 105] = "Numpad9";
    Key[Key["Multiply"] = 106] = "Multiply";
    Key[Key["Add"] = 107] = "Add";
    Key[Key["Subtract"] = 109] = "Subtract";
    Key[Key["DecimalPoint"] = 110] = "DecimalPoint";
    Key[Key["Divide"] = 111] = "Divide";
    Key[Key["F1"] = 112] = "F1";
    Key[Key["F2"] = 113] = "F2";
    Key[Key["F3"] = 114] = "F3";
    Key[Key["F4"] = 115] = "F4";
    Key[Key["F5"] = 116] = "F5";
    Key[Key["F6"] = 117] = "F6";
    Key[Key["F7"] = 118] = "F7";
    Key[Key["F8"] = 119] = "F8";
    Key[Key["F9"] = 120] = "F9";
    Key[Key["F10"] = 121] = "F10";
    Key[Key["F11"] = 122] = "F11";
    Key[Key["F12"] = 123] = "F12";
    Key[Key["NumLock"] = 144] = "NumLock";
    Key[Key["ScrollLock"] = 145] = "ScrollLock";
    Key[Key["SemiColon"] = 186] = "SemiColon";
    Key[Key["Equals"] = 187] = "Equals";
    Key[Key["Comma"] = 188] = "Comma";
    Key[Key["Dash"] = 189] = "Dash";
    Key[Key["Period"] = 190] = "Period";
    Key[Key["UnderScore"] = 189] = "UnderScore";
    Key[Key["PlusSign"] = 187] = "PlusSign";
    Key[Key["ForwardSlash"] = 191] = "ForwardSlash";
    Key[Key["Tilde"] = 192] = "Tilde";
    Key[Key["GraveAccent"] = 192] = "GraveAccent";
    Key[Key["OpenBracket"] = 219] = "OpenBracket";
    Key[Key["ClosedBracket"] = 221] = "ClosedBracket";
    Key[Key["Quote"] = 222] = "Quote";
})(Key || (Key = {}));

//#region Imports
//#endregion
/**
 * @public
 */
class Length {
    constructor(...value) {
        let v = null;
        let u = null;
        if (value.length === 1) {
            if (typeof value[0] === 'string') {
                v = Number.parseInt(value[0], 10);
                u = value[0].substring(0, value.length);
            }
        }
        if (value.length === 2) {
            if (typeof value[0] === 'number' && typeof value[1] === 'string') {
                v = value[0];
                if (Length._units.map(x => x.toString()).includes(value[1])) {
                    u = value[1];
                }
                else {
                    throw new ArgumentException('Could not parse Unit.', 'unit');
                }
            }
        }
        if (v !== null && v !== undefined && u !== null && u !== undefined) {
            this._value = v;
            this._unit = u;
        }
        else {
            throw new ArgumentException('Could not parse Arguments.', 'value');
        }
    }
    //#endregion
    //#region Properies
    get value() { return this._value; }
    set value(value) { this._value = value; }
    get unit() { return this._unit; }
    set unit(value) { this._unit = value; }
    static parse(...value) {
        if (value.length === 1) {
            return new Length(value[0]);
        }
        if (value.length === 2) {
            return new Length(value[0], value[1]);
        }
        throw new ArgumentException('Could not parse Arguments.', 'value');
    }
    toString() {
        return `${this._value}${this._unit}`;
    }
}
//#region Fields
Length._units = ['px', 'cm', 'mm', '%', 'ch', 'in', 'em', 'rem', 'pt', 'pc', 'ex', 'vw', 'vh', 'vmin', 'vmax', 'deg', 'turn', 'rad', 'grad', 's', 'ms', 'Hz', 'kHz', 'dppx', 'dpcm', 'dpi'];

/**
 * @public
 */
class Point {
    //#endregion
    //#region Ctor
    constructor(x, y) {
        this._x = x;
        this._y = y;
    }
    //#endregion
    //#region Properties
    get isEmpty() {
        return this._x === 0 && this._y === 0;
    }
    get x() {
        return this._x;
    }
    set x(value) {
        this._x = value;
    }
    get y() {
        return this._y;
    }
    set y(value) {
        this._y = value;
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class Size {
    //#endregion
    //#region Ctor
    constructor(width, height) {
        if (width < 0 || height < 0) {
            throw new Exception('\'width\' and \'height\' cannot be negative.');
        }
        this._width = width;
        this._height = height;
    }
    //#endregion
    //#region Properties
    get isEmpty() {
        return this._width === 0;
    }
    get width() {
        return this._width;
    }
    set width(value) {
        if (value < 0) {
            throw new Exception('\'width\' cannot be negative.');
        }
        this._width = value;
    }
    get height() {
        return this._height;
    }
    set height(value) {
        if (value < 0) {
            throw new Exception('\'height\' cannot be negative.');
        }
        this._height = value;
    }
    //#endregion
    //#region Methods
    reset(width, height) {
        this.width = width || 0;
        this.height = height || 0;
        return this;
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class RAF {
    //#endregion
    //#region Methods
    static init(window) {
        RAF._request = window.requestAnimationFrame;
        RAF._cancel = window.cancelAnimationFrame;
        RAF._lastTime = 0;
        if (!RAF._request) {
            RAF._request = (callback) => {
                const currTime = Date.now();
                const timeToCall = Math.max(0, 16 - (currTime - RAF._lastTime));
                const token = setTimeout(() => { callback(currTime + timeToCall); }, timeToCall);
                RAF._lastTime = currTime + timeToCall;
                return token;
            };
            RAF._cancel = clearTimeout;
        }
        return { request: RAF._request, cancel: RAF._cancel };
    }
}

/**
 * @public
 */
class Color {
    //#endregion
    //#region Ctor
    constructor() {
        this._r = 0;
        this._g = 0;
        this._b = 0;
        this._a = 1;
    }
    //#endregion
    //#region Properties
    get r() {
        return this._r;
    }
    set r(value) {
        Guard.isLessThanOrGreaterThan(0, 255, value, 'value');
        this._r = value;
    }
    get g() {
        return this._g;
    }
    set g(value) {
        Guard.isLessThanOrGreaterThan(0, 255, value, 'value');
        this._g = value;
    }
    get b() {
        return this._b;
    }
    set b(value) {
        Guard.isLessThanOrGreaterThan(0, 255, value, 'value');
        this._b = value;
    }
    get a() {
        return this._a;
    }
    set a(value) {
        Guard.isLessThanOrGreaterThan(0, 255, value, 'value');
        this._a = value;
    }
    //#endregion
    //#region Methods
    static lerp(start, end, p) {
        const c = new Color();
        c.r = start.r + (end.r - start.r) * p;
        c.g = start.g + (end.g - start.g) * p;
        c.b = start.b + (end.b - start.b) * p;
        c.a = start.a + (end.a - start.a) * p;
        return c;
    }
    static fromArgb(a, r, g, b) {
        const c = new Color();
        c.a = a;
        c.r = r;
        c.g = g;
        c.b = b;
        return c;
    }
    static fromRgb(r, g, b) {
        return Color.fromArgb(1, r, g, b);
    }
    static fromHex(hex) {
        let match;
        const c = new Color();
        // tslint:disable-next-line:no-conditional-assignment
        if ((match = Color._alphaRegex.exec(hex)) != null) {
            c.a = parseInt(match[1], 16) / 255;
            c.r = parseInt(match[2], 16);
            c.g = parseInt(match[3], 16);
            c.b = parseInt(match[4], 16);
            // tslint:disable-next-line:no-conditional-assignment
        }
        else if ((match = Color._noAlphaRegex.exec(hex)) != null) {
            c.a = 1;
            c.r = parseInt(match[1], 16);
            c.g = parseInt(match[2], 16);
            c.b = parseInt(match[3], 16);
        }
        return c;
    }
    add(color2) {
        const c = new Color();
        c.r = this.r + color2.r;
        c.g = this.g + color2.g;
        c.b = this.b + color2.b;
        c.a = this.a + color2.a;
        return c;
    }
    subtract(color2) {
        const c = new Color();
        c.r = this.r - color2.r;
        c.g = this.g - color2.g;
        c.b = this.b - color2.b;
        c.a = this.a - color2.a;
        return c;
    }
    multiply(factor) {
        const c = new Color();
        c.r = this.r * factor;
        c.g = this.g * factor;
        c.b = this.b * factor;
        c.a = this.a * factor;
        return c;
    }
    equals(other) {
        return this.r === other.r && this.g === other.g && this.b === other.b && this.a === other.a;
    }
    toString() {
        const r = Math.round(this.r) || 0;
        const g = Math.round(this.g) || 0;
        const b = Math.round(this.b) || 0;
        const a = this.a || 0;
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }
    toHexStringNoAlpha() {
        return '#' + this.r.toString(16) + this.g.toString(16) + this.b.toString(16);
    }
    toHexString() {
        return '#' + (this.a * 255).toString(16) + this.r.toString(16) + this.g.toString(16) + this.b.toString(16);
    }
}
//#region Fields
Color._noAlphaRegex = /#([0-9a-fA-F][0-9a-fA-F]){1}([0-9a-fA-F][0-9a-fA-F]){1}([0-9a-fA-F][0-9a-fA-F]){1}/;
Color._alphaRegex = /#([0-9a-fA-F][0-9a-fA-F]){1}([0-9a-fA-F][0-9a-fA-F]){1}([0-9a-fA-F][0-9a-fA-F]){1}([0-9a-fA-F][0-9a-fA-F]){1}/;

/**
 * `DisplayDensity` - Enum which describes 3 possible density options.
 * @public
 */
var DisplayDensity;
(function (DisplayDensity) {
    /**
     * `comfortable` - normal or medium
     */
    DisplayDensity["Comfortable"] = "comfortable";
    /**
     * `cosy` - large
     */
    DisplayDensity["Cosy"] = "cosy";
    /**
     * `compact` - small
     */
    DisplayDensity["Compact"] = "compact";
})(DisplayDensity || (DisplayDensity = {}));

/**
 * @public
 */
class MessageBoxButton {
}
MessageBoxButton.ok = 0;
MessageBoxButton.cancel = 1;
MessageBoxButton.yes = 2;
MessageBoxButton.no = 3;
MessageBoxButton.abort = 4;
MessageBoxButton.retry = 5;
MessageBoxButton.ignore = 6;
/**
 * @public
 */
class MessageBoxButtons {
}
MessageBoxButtons.abortRetryIgnore = [MessageBoxButton.abort, MessageBoxButton.retry, MessageBoxButton.ignore];
MessageBoxButtons.ok = [MessageBoxButton.ok];
MessageBoxButtons.okCancel = [MessageBoxButton.ok, MessageBoxButton.cancel];
MessageBoxButtons.retryCancel = [MessageBoxButton.retry, MessageBoxButton.cancel];
MessageBoxButtons.yesNo = [MessageBoxButton.yes, MessageBoxButton.no];
MessageBoxButtons.yesNoCancel = [MessageBoxButton.yes, MessageBoxButton.no, MessageBoxButton.cancel];
/**
 * @public
 */
class MessageBoxIcon {
}
MessageBoxIcon.asterisk = 0;
MessageBoxIcon.error = 1;
MessageBoxIcon.exclamation = 2;
MessageBoxIcon.hand = 3;
MessageBoxIcon.information = 4;
MessageBoxIcon.none = 5;
MessageBoxIcon.question = 6;
MessageBoxIcon.stop = 7;
MessageBoxIcon.warning = 8;

//#region Imports
//#endregion
/**
 * @public
 */
class MediaQueryChangedEventArgs extends EventArgs {
    //#endregion
    //#region Ctor
    constructor(rule, active) {
        super();
        this._active = active;
        this._rule = rule;
    }
    //#endregion
    //#region Properties
    get active() { return this._active; }
    get rule() { return this._rule; }
}

//#region Imports
//#endregion
/**
 * @public
 */
class MediaQueryRule {
    //#endregion
    //#region Ctor
    constructor(key, query, description) {
        this._description = description || '';
        this._query = query;
        this._key = key;
    }
    //#endregion
    //#region Properties
    get key() {
        return this._key;
    }
    get query() {
        return this._query;
    }
    get description() {
        return this._description;
    }
    //#endregion
    //#region Methods
    toString() {
        const sb = new StringBuilder();
        sb.append(`${this._key}: ${this._description}`);
        sb.appendLine(this._query.toString());
        return sb.toString();
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class ApplicationServiceEvents {
    //#endregion
    //#region Ctor
    constructor(events) {
        this._loading = events.loading;
        this._interactive = events.interactive;
        this._complete = events.complete;
        this._resized = events.resized;
    }
    //#endregion
    //#region Properties
    get loading() { return this._loading; }
    get interactive() { return this._interactive; }
    get complete() { return this._complete; }
    get resized() { return this._resized; }
}

//#region Imports
//#endregion
/**
 * @public
 */
class ApplicationService {
    //#endregion
    //#region Ctor
    constructor(eventAggregator) {
        this._eventAggregator = eventAggregator;
        this._params = new Map();
        this._events = new ApplicationServiceEvents({
            loading: this._eventAggregator.getEvent(ApplicationService.events.loading),
            interactive: this._eventAggregator.getEvent(ApplicationService.events.interactive),
            complete: this._eventAggregator.getEvent(ApplicationService.events.complete),
            resized: this._eventAggregator.getEvent(ApplicationService.events.resized)
        });
    }
    //#endregion
    //#region Properties
    get events() {
        return this._events;
    }
    get title() {
        return this._window ? this._window.document.title : '';
    }
    set title(value) {
        if (this._window !== undefined) {
            this._window.document.title = value;
        }
    }
    get nativeWindow() {
        if (this._window !== undefined) {
            return this._window;
        }
        else {
            throw new Exception(`The ApplicationService is not initialized.`);
        }
    }
    get nativeDocument() {
        if (this._document !== undefined) {
            return this._document;
        }
        else {
            throw new Exception(`The ApplicationService is not initialized.`);
        }
    }
    get isWindowAccessible() {
        return window !== undefined;
    }
    get isDocumentAccessible() {
        return document !== undefined;
    }
    //#endregion
    //#region Methods
    initialize() {
        if (this._window) {
            this._window.document.addEventListener('readystatechange', (x) => this.onReadystateChanged(x));
            this._window.addEventListener('resize', (x) => this.onResized(x));
        }
        else {
            throw new Exception(`No window is registered. Call 'register' method first.`);
        }
    }
    setParam(key, value) {
        this._params.set(key, value);
    }
    getParam(key) {
        return this._params.get(key);
    }
    register(window, params) {
        this._window = window;
        this._document = window.document;
        if (params) {
            Object.keys(params).forEach(x => this.setParam(x, params[x]));
        }
        return this;
    }
    focus() {
        if (this._window !== undefined) {
            this._window.focus();
        }
    }
    close() {
        if (this._window !== undefined) {
            this._window.close();
        }
    }
    onReadystateChanged(event) {
        if (this._window) {
            if (this._window.document.readyState === 'interactive') {
                this._events.interactive.publish(this._window.document.readyState);
            }
            else if (this._window.document.readyState === 'complete') {
                this._events.complete.publish(this._window.document.readyState);
            }
            else if (this._window.document.readyState === 'loading') {
                this._events.loading.publish(this._window.document.readyState);
            }
        }
    }
    onResized(event) {
        this._events.resized.publish(new Size(this._window.innerWidth, this._window.innerHeight));
    }
}
ApplicationService.events = {
    loading: 'loaingEvent',
    interactive: 'interactiveEvent',
    complete: 'completeEvent',
    resized: 'resizedEvent'
};

/**
 * @public
 */
class BusyService {
    //#endregion
    //#region Ctor
    constructor() {
        //#region Fields
        this._subscriptionCount = 0;
        this._isBusy = false;
        this.busyChangedEvent = new Event(this._eventInvoker = new EventInvoker());
    }
    //#endregion
    //#region Properties
    get isBusy() {
        return this._isBusy;
    }
    //#endregion
    //#region Methods
    beginScope(scopeName, action) {
        Guard.isNotNullOrEmpty(scopeName, 'scopeName');
        Guard.isNotValid(typeof scopeName !== 'string', 'scopeName');
        Guard.isNotNull(action, 'action');
        Guard.isNotValid(typeof action !== 'function', 'action');
        let promise;
        try {
            this.setBusy(scopeName, true);
        }
        finally {
            promise = action();
            if (promise && promise.then) {
                promise.then(() => this.setBusy(scopeName, false)).catch(error => {
                    this.setBusy(scopeName, false);
                    return Promise.reject(error);
                });
            }
            else {
                this.setBusy(scopeName, false);
            }
        }
    }
    setBusy(scopeName, isBusy) {
        Guard.isNotNullOrEmpty(scopeName, 'scopeName');
        Guard.isNotValid(typeof scopeName !== 'string', 'scopeName');
        Guard.isNotValid(typeof isBusy !== 'boolean', 'isBusy');
        this._subscriptionCount += isBusy ? 1 : -1;
        if (isBusy) {
            this._isBusy = true;
        }
        else if (this._subscriptionCount === 0) {
            this._isBusy = false;
        }
        isBusy = this._isBusy;
        if (this._subscriptionCount < 0) {
            throw new Exception('The subscription count should not be negative.');
        }
        this._eventInvoker.invoke(this, new ValueEventArgs({ scopeName, isBusy }));
    }
}

// @see https://github.com/IgniteUI/igniteui-angular/blob/master/projects/igniteui-angular/src/lib/services/exporter-common/export-utilities.ts
//#endregion
/**
 * @public
 */
class FileService {
    //#region Methods
    saveFile(source, fileName) {
        return __awaiter(this, void 0, void 0, function* () {
            const blob = yield source;
            const anchorElement = document.createElement('a');
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                // IE
                window.navigator.msSaveOrOpenBlob(blob, fileName);
            }
            else {
                const url = window.URL.createObjectURL(blob);
                anchorElement.download = fileName;
                anchorElement.href = url;
                document.body.appendChild(anchorElement);
                anchorElement.click();
                document.body.removeChild(anchorElement);
                window.URL.revokeObjectURL(url);
            }
        });
    }
}

//#region Imports
//#endregion
/**
 * @public
 */
class MediaQueryServiceEvents {
    //#endregion
    //#region Ctor
    constructor(events) {
        this._mediaQuerySizeChanged = events.mediaQuerySizeChanged;
    }
    //#endregion
    //#region Properties
    get mediaQuerySizeChanged() { return this._mediaQuerySizeChanged; }
}

/**
 * @public
 */
class MediaQueryService {
    //#endregion
    //#region Ctor
    constructor(eventAggregator) {
        this._eventAggregator = eventAggregator;
        this._mediaQueryLists = new Array();
        this._mediaRules = new Array();
        this._mediaRules.push(new MediaQueryRule('xs', '(max-width: 599px)', 'extra small'));
        this._mediaRules.push(new MediaQueryRule('gt-xs', '(min-width: 600px)', 'greater than extra small'));
        this._mediaRules.push(new MediaQueryRule('lt-sm', '(max-width: 599px)', 'less than small'));
        this._mediaRules.push(new MediaQueryRule('sm', '(max-width: 959px) and (min-width: 600px)', 'small'));
        this._mediaRules.push(new MediaQueryRule('gt-sm', '(min-width: 960px)', 'greater than small'));
        this._mediaRules.push(new MediaQueryRule('lt-md', '(max-width: 959px)', 'less than medium'));
        this._mediaRules.push(new MediaQueryRule('md', '(max-width: 1279px) and (min-width: 960px)', 'medium'));
        this._mediaRules.push(new MediaQueryRule('gt-md', '(min-width: 1280px)', 'greater than medium'));
        this._mediaRules.push(new MediaQueryRule('lt-lg', '(max-width: 1279px)', 'less than large'));
        this._mediaRules.push(new MediaQueryRule('lg', '(max-width: 1919px) and (min-width: 1280px)', 'large'));
        this._mediaRules.push(new MediaQueryRule('gt-lg', '(min-width: 1920px)', 'greater than large'));
        this._mediaRules.push(new MediaQueryRule('lt-xl', '(max-width: 1919px)', 'less than extra large'));
        this._mediaRules.push(new MediaQueryRule('xl', '(min-width: 1920px)', 'extra large'));
        this._mediaRules.push(new MediaQueryRule('landscape', '(orientation: landscape)'));
        this._mediaRules.push(new MediaQueryRule('portrait', '(orientation: portrait)'));
        this._mediaRules.push(new MediaQueryRule('print', 'print'));
        this._events = new MediaQueryServiceEvents({
            mediaQuerySizeChanged: this._eventAggregator.getEvent(MediaQueryService.events.mediaQuerySizeChanged)
        });
    }
    //#endregion
    //#region Properties
    get events() {
        return this._events;
    }
    get rules() {
        return this._mediaRules;
    }
    //#endregion
    //#region Methods
    add(key, query, description) {
        this.addRule(new MediaQueryRule(key, query, description));
    }
    addRule(rule) {
        this._mediaRules.push(rule);
        this.registerQuery(rule.query);
    }
    is(key) {
        const rule = this._mediaRules.find(x => x.key === key);
        if (rule) {
            return matchMedia(rule.query).matches;
        }
        else {
            return false;
        }
    }
    active() {
        const activeMatches = Array();
        this._mediaRules.forEach(x => {
            if (matchMedia(x.query).matches) {
                activeMatches.push(x.key);
            }
        });
        return activeMatches;
    }
    initialize() {
        this._mediaRules.forEach(rule => {
            this.registerQuery(rule.query);
        });
    }
    dispose() {
        this._mediaQueryLists.forEach(list => MediaQueryListExtensions.removeChangeListener(list, x => this.onQueryChanged(x)));
    }
    registerQuery(query) {
        const mediaQueryList = matchMedia(query);
        MediaQueryListExtensions.addChangeListener(mediaQueryList, x => this.onQueryChanged(x));
        this._mediaQueryLists.push(mediaQueryList);
    }
    onQueryChanged(query) {
        const rule = this._mediaRules.find(x => x.query === query.media);
        if (rule) {
            this._events.mediaQuerySizeChanged.publish(new MediaQueryChangedEventArgs(rule, this.is(rule.key)));
        }
    }
}
MediaQueryService.events = {
    mediaQuerySizeChanged: 'mediaQuerySizeChangedEvent'
};

export { ApplicationService, ApplicationServiceEvents, BusyService, Color, Command, CommandAggregator, DOMTokenListExtensions, DisplayDensity, ElementExtensions, FileService, Key, Length, MediaQueryChangedEventArgs, MediaQueryListExtensions, MediaQueryRule, MediaQueryService, MediaQueryServiceEvents, MessageBoxButton, MessageBoxButtons, MessageBoxIcon, NamedNodeMapExtensions, Point, PropertyChangedEventArgs, QuerySelectorElementException, RAF, Size, StringExtensions };
